import { useEffect, useState } from 'react'
import ResellerShell from './ResellerShell'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'
import { mdToHtml } from '../../components/MarkdownField'
import { BilingualRichText, sanitizeRichHtml } from '../../components/RichTextField'
import { TERMS_FALLBACK_MD } from '../../config/termsContent'

/**
 * Reseller-authored Terms of Use for their child accounts (EPIC — child terms).
 * Only reachable when a super-admin has enabled authoring for this account.
 * The parent copies InteGolf's latest terms, edits (EN/FR), and publishes
 * versions that apply to their direct children — accepting publishing
 * responsibility each time.
 */
export default function ChildTerms() {
  const { t } = useLanguage()
  const [versions, setVersions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [acceptancesFor, setAcceptancesFor] = useState(null)
  const [forbidden, setForbidden] = useState(false)

  function reload() {
    setLoading(true); setError(null)
    tournamentApi.get('/portal/reseller/child-terms/versions')
      .then(r => setVersions(r.data?.versions || []))
      .catch(e => {
        if (e?.response?.status === 403) setForbidden(true)
        else setError(e?.response?.data?.message || e.message)
      })
      .finally(() => setLoading(false))
  }
  useEffect(reload, [])

  if (forbidden) {
    return (
      <ResellerShell title={t('sp_child_terms_title', 'Child Terms of Use')}>
        <div style={infoBox}>{t('sp_child_terms_not_enabled', 'Authoring your own terms is not enabled for your account. Contact InteGolf if you need it.')}</div>
      </ResellerShell>
    )
  }

  return (
    <ResellerShell title={t('sp_child_terms_title', 'Child Terms of Use')}>
      <p style={{ color: '#64748b', maxWidth: 760, marginTop: 0, fontSize: 14, lineHeight: 1.6 }}>
        {t('sp_child_terms_intro', "Publish your own Terms of Use for the accounts you invite. Start from InteGolf's latest terms and edit them to suit your needs. Publishing a new version requires your child accounts to accept it again.")}
      </p>

      <div style={{ marginBottom: 20 }}>
        <button type="button" onClick={() => setShowForm(s => !s)} style={primaryBtn}>
          {showForm ? t('cancel', 'Cancel') : t('sp_child_terms_publish_new', '+ Publish new version')}
        </button>
      </div>

      {showForm && <PublishForm latestVersion={versions[0] || null} onPublished={() => { setShowForm(false); reload() }} />}

      {error && <div style={errorBox}>{error}</div>}

      {loading ? <div style={empty}>{t('sp_loading_short', 'Loading…')}</div> : (
        <div style={tableWrap}>
          <table style={tbl}>
            <thead>
              <tr>
                <th style={th}>{t('sp_terms_version_label', 'Version')}</th>
                <th style={th}>{t('sp_terms_col_title', 'Title')}</th>
                <th style={th}>{t('sp_terms_col_effective', 'Effective')}</th>
                <th style={th}>{t('sp_terms_col_current', 'Current')}</th>
                <th style={th}>{t('sp_terms_col_acceptances', 'Acceptances')}</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {versions.map(v => (
                <tr key={v.id} style={tr}>
                  <td style={td}><strong>{v.version}</strong></td>
                  <td style={td}>{v.title || '—'}</td>
                  <td style={td}>{v.effectiveDate ? new Date(v.effectiveDate).toLocaleDateString() : '—'}</td>
                  <td style={td}>{v.isCurrent ? <span style={badge}>{t('sp_terms_current', 'Current')}</span> : ''}</td>
                  <td style={td}>{v.acceptCount}</td>
                  <td style={td}><button type="button" onClick={() => setAcceptancesFor(v)} style={secondaryBtn}>{t('sp_terms_view_acceptances', 'View acceptances')}</button></td>
                </tr>
              ))}
              {versions.length === 0 && <tr><td style={td} colSpan={6}>{t('sp_child_terms_none', 'No versions published yet.')}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {acceptancesFor && <AcceptancesModal version={acceptancesFor} onClose={() => setAcceptancesFor(null)} />}
    </ResellerShell>
  )
}

function PublishForm({ onPublished, latestVersion }) {
  const { t } = useLanguage()
  const [version, setVersion] = useState('')
  const [effectiveDate, setEffectiveDate] = useState('')
  const [titleEn, setTitleEn] = useState('Terms of Use')
  const [titleFr, setTitleFr] = useState('Conditions d’utilisation')
  const [contentEn, setContentEn] = useState('')
  const [contentFr, setContentFr] = useState('')
  const [summaryEn, setSummaryEn] = useState('')
  const [summaryFr, setSummaryFr] = useState('')
  const [agree, setAgree] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  // Bumped whenever we programmatically replace the editor content (seed), so
  // the visual editor reloads it without disturbing the caret while typing.
  const [reseedKey, setReseedKey] = useState(0)

  // The visual editor works in HTML; convert whatever we seed (Markdown or
  // HTML) to HTML so it renders formatted, not as raw symbols.
  function applySeed(enHtml, frHtml) {
    setContentEn(enHtml || '')
    setContentFr(frHtml || '')
    setReseedKey(k => k + 1)
  }

  function seedFromIntegolf() {
    tournamentApi.get('/public/terms/current')
      .then(r => {
        const v = r.data?.currentVersion
        const rawEn = (v?.contentMd && v.contentMd.trim()) ? v.contentMd : TERMS_FALLBACK_MD
        applySeed(mdToHtml(rawEn), v?.contentMdFr ? mdToHtml(v.contentMdFr) : '')
        if (v?.title) setTitleEn(v.title)
        if (v?.titleFr) setTitleFr(v.titleFr)
      })
      .catch(() => applySeed(mdToHtml(TERMS_FALLBACK_MD), ''))
  }

  // Pre-fill on open so the owner edits a copy rather than a blank box:
  // their own most-recent version if they have one, otherwise InteGolf's latest.
  useEffect(() => {
    if (latestVersion) {
      applySeed(mdToHtml(latestVersion.contentMd || ''), latestVersion.contentMdFr ? mdToHtml(latestVersion.contentMdFr) : '')
      if (latestVersion.title) setTitleEn(latestVersion.title)
      if (latestVersion.titleFr) setTitleFr(latestVersion.titleFr)
    } else {
      seedFromIntegolf()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function submit(e) {
    e.preventDefault()
    if (!version.trim()) { setErr(t('sp_terms_version_required', 'A version label is required.')); return }
    if (!agree) { setErr(t('sp_child_terms_must_ack', 'You must accept publishing responsibility to continue.')); return }
    setSaving(true); setErr(null)
    try {
      await tournamentApi.post('/portal/reseller/child-terms/versions', {
        version: version.trim(),
        title: titleEn.trim() || undefined,
        titleFr: titleFr.trim() || undefined,
        summary: summaryEn.trim() || undefined,
        summaryFr: summaryFr.trim() || undefined,
        contentMd: contentEn.trim() ? sanitizeRichHtml(contentEn) : undefined,
        contentMdFr: contentFr.trim() ? sanitizeRichHtml(contentFr) : undefined,
        effectiveDate: effectiveDate || undefined,
        acceptedResponsibility: true,
      })
      onPublished?.()
    } catch (e2) { setErr(e2?.response?.data?.message || e2.message) }
    finally { setSaving(false) }
  }

  return (
    <form onSubmit={submit} style={formCard}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Field label={t('sp_terms_version_field', 'Version label (e.g. 2026-07)')} style={{ flex: '1 1 200px' }}>
          <input value={version} onChange={e => setVersion(e.target.value)} required style={inp} placeholder="2026-07" />
        </Field>
        <Field label={t('sp_terms_effective_optional', 'Effective date (optional)')} style={{ flex: '1 1 200px' }}>
          <input type="datetime-local" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} style={inp} />
        </Field>
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Field label={t('sp_terms_title_en', 'Title (English)')} style={{ flex: '1 1 200px' }}>
          <input value={titleEn} onChange={e => setTitleEn(e.target.value)} style={inp} />
        </Field>
        <Field label={t('sp_terms_title_fr', 'Title (French)')} style={{ flex: '1 1 200px' }}>
          <input value={titleFr} onChange={e => setTitleFr(e.target.value)} style={inp} />
        </Field>
      </div>

      <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <label style={lbl}>{t('sp_child_terms_content', 'Terms content')}</label>
        <button type="button" onClick={seedFromIntegolf} style={secondaryBtn} title={t('sp_child_terms_seed_hint', "Replace the content below with InteGolf's latest terms")}>
          {t('sp_child_terms_seed', "Start from InteGolf's latest")}
        </button>
      </div>
      <BilingualRichText
        valueEn={contentEn} valueFr={contentFr}
        onChangeEn={setContentEn} onChangeFr={setContentFr}
        reseedKey={reseedKey}
      />

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 14 }}>
        <Field label={t('sp_terms_summary_en', 'Summary of changes (English)')} style={{ flex: '1 1 200px' }}>
          <input value={summaryEn} onChange={e => setSummaryEn(e.target.value)} style={inp} />
        </Field>
        <Field label={t('sp_terms_summary_fr', 'Summary of changes (French)')} style={{ flex: '1 1 200px' }}>
          <input value={summaryFr} onChange={e => setSummaryFr(e.target.value)} style={inp} />
        </Field>
      </div>

      <label style={ackRow}>
        <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} style={{ marginTop: 3 }} />
        <span>{t('sp_child_terms_responsibility', 'By creating and publishing your own Terms of Use, you accept full responsibility for the agreement between you and your child resellers. These terms are solely between you and your child accounts — InteGolf is not a party to them and is not responsible for their content or enforcement.')}</span>
      </label>

      {err && <div style={errorBox}>{err}</div>}
      <div style={{ marginTop: 12 }}>
        <button type="submit" disabled={saving || !agree} style={{ ...primaryBtn, opacity: (saving || !agree) ? 0.6 : 1 }}>
          {saving ? t('sp_sending_ellipsis', 'Sending…') : t('sp_child_terms_publish_make_current', 'Publish and make current')}
        </button>
      </div>
      <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>
        {t('sp_child_terms_publish_note', 'Publishing makes this the current version and requires your child accounts to accept it again.')}
      </p>
    </form>
  )
}

function AcceptancesModal({ version, onClose }) {
  const { t } = useLanguage()
  const [rows, setRows] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => {
    tournamentApi.get(`/portal/reseller/child-terms/versions/${version.id}/acceptances`)
      .then(r => setRows(r.data?.acceptances || []))
      .catch(e => setError(e?.response?.data?.message || e.message))
  }, [version.id])
  return (
    <div style={overlay} onClick={onClose}>
      <div style={modalCard} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>{t('sp_terms_acceptances_for', 'Acceptances')} — {version.version}</h3>
          <button type="button" onClick={onClose} style={secondaryBtn}>{t('sp_managed_dismiss', 'Close')}</button>
        </div>
        {error && <div style={errorBox}>{error}</div>}
        {!rows ? <div style={empty}>{t('sp_loading_short', 'Loading…')}</div> : rows.length === 0 ? <div style={empty}>{t('sp_terms_no_acceptances', 'No acceptances yet.')}</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tbl}>
              <thead><tr>
                <th style={th}>{t('sp_terms_col_account', 'Account')}</th>
                <th style={th}>{t('sp_email', 'Email')}</th>
                <th style={th}>{t('sp_terms_col_when', 'When')}</th>
                <th style={th}>{t('sp_terms_col_ip', 'IP')}</th>
              </tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} style={tr}>
                    <td style={td}>{r.accountName || r.accountId || '—'}</td>
                    <td style={td}>{r.email}{r.name ? <div style={{ fontSize: 12, color: '#64748b' }}>{r.name}</div> : null}</td>
                    <td style={td}>{r.acceptedAt ? new Date(r.acceptedAt).toLocaleString() : '—'}</td>
                    <td style={td}>{r.ip || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function Field({ label, children, style }) {
  return (
    <div style={{ marginBottom: 14, ...style }}>
      <label style={lbl}>{label}</label>
      {children}
    </div>
  )
}

const lbl = { display: 'block', fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 6 }
const primaryBtn = { padding: '10px 18px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn = { padding: '6px 12px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: 'pointer' }
const formCard = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 20, marginBottom: 24, maxWidth: 880 }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, margin: '8px 0', fontSize: 13 }
const infoBox = { padding: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#475569', lineHeight: 1.5 }
const empty = { padding: 30, textAlign: 'center', color: '#64748b' }
const tableWrap = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflowX: 'auto' }
const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 14 }
const tr = { borderBottom: '1px solid #f1f5f9' }
const th = { padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase', background: '#f8fafc' }
const td = { padding: '12px 16px', verticalAlign: 'top' }
const badge = { padding: '3px 8px', background: '#dcfce7', color: '#166534', borderRadius: 999, fontSize: 11, fontWeight: 600 }
const inp = { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }
const ackRow = { display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 16, padding: 12, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#92400e', lineHeight: 1.5, cursor: 'pointer' }
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }
const modalCard = { background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 820, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }
